import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  useExplore,
  toCard,
  railTrending,
  railLocals,
  railEatDrink,
  railChinchorros,
  photosFirst } from
'@/lib/ExploreContext';
import Hero from '@/components/yts/Hero';
import HomeSearchPill from '@/components/yts/HomeSearchPill';
import HomeLiveMoment from '@/components/yts/HomeLiveMoment';
import HomeChapterBreak from '@/components/yts/HomeChapterBreak';
import CategoryNav from '@/components/yts/CategoryNav';
const ElBoricuaFeature = lazy(() => import('@/components/yts/ElBoricuaFeature'));
import NearMeCard from '@/components/yts/NearMeCard';
import HotelsRail from '@/components/realestate/HotelsRail';
import CategoryView from '@/components/yts/CategoryView';
import ExperiencesCategoryView from '@/components/yts/ExperiencesCategoryView';
const LGBTQGuide = lazy(() => import('@/pages/LGBTQGuide'));
import { EXPERIENCES_CATEGORY } from '@/data/experiences';
import ExperienceCard from '@/components/yts/ExperienceCard';
import NeighborhoodCard from '@/components/yts/NeighborhoodCard';
import ResponsiveRail from '@/components/yts/ResponsiveRail';
import CarouselRail from '@/components/yts/CarouselRail';
const HomeArtGalleriesRail = lazy(() => import('@/components/yts/HomeArtGalleriesRail'));
import HomeMakeADay from '@/components/yts/HomeMakeADay';
const HomeLGBTQVenuesRail = lazy(() => import('@/components/yts/HomeLGBTQVenuesRail'));
import HomeBookingEdit from '@/components/yts/HomeBookingEdit';
import BombaSalsaHomeRail from '@/components/yts/BombaSalsaHomeRail';
import HorsebackHomeRail from '@/components/yts/HorsebackHomeRail';
const FreshFindsRail = lazy(() => import('@/components/yts/FreshFindsRail'));
const HotelsDirectory = lazy(() => import('@/pages/Hotels'));
const AutoDirectory = lazy(() => import('@/pages/Auto'));
const JobsDirectory = lazy(() => import('@/pages/JobsPortal').then((module) => ({ default: module.JobsPortal })));
const RainyDayRail = lazy(() => import('@/components/yts/RainyDayRail'));
const OffBeatenPathRail = lazy(() => import('@/components/yts/OffBeatenPathRail'));
const BeachRail = lazy(() => import('@/components/yts/BeachRail'));
import BeachCategoryView from '@/components/yts/BeachCategoryView';
import ServicesCategoryView from '@/components/yts/ServicesCategoryView';
import { CATEGORIES, SERVICES_CATEGORY, catMatches } from '@/data/taxonomy';
import { BEACH_CATEGORY } from '@/data/beaches';
import SectionHead from '@/components/yts/SectionHead';
import ResultsSort from '@/components/yts/results/ResultsSort';
import KeepExploring from '@/components/yts/results/KeepExploring';
import { useLang } from '@/lib/i18n';
import HomeRowAd from '@/components/yts/HomeRowAd';
const HouseOfPhobiaBanner = lazy(() => import('@/components/yts/HouseOfPhobiaBanner'));
const CollectionRail = lazy(() => import('@/components/yts/CollectionRail'));
import FeaturedEventsRail from '@/components/yts/FeaturedEventsRail';
import DeferredMount from '@/components/yts/DeferredMount';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { StaticSection } from '@/components/yts/HomeSections';
import SEO from '@/components/SEO';
import HomeJobsRail from '@/components/yts/HomeJobsRail';
import RioPiedrasRail from '@/components/yts/RioPiedrasRail';
import BadBunnyFeature from '@/components/yts/BadBunnyFeature';
import RentalCarsRail from '@/components/yts/RentalCarsRail';
import PrideFeature from '@/components/yts/PrideFeature';
const NightlifeRails = lazy(() => import('@/components/yts/NightlifeRails'));
import HomeKaraokeRail from '@/components/yts/HomeKaraokeRail';
const CondadoDiningEdit = lazy(() => import('@/components/yts/CondadoDiningEdit'));

const RAIL_MAX = 10;

const BROWSE_SECTION_TITLES = {
  Gastronomy: "Gastronomy",
  "Eat & Drink": "Gastronomy",
  Eat: "Gastronomy",
  Drink: "Gastronomy",
  Nightlife: "Nightlife",
  Shop: "Local Shops",
  Experiences: "Tours & Adventures",
  Services: "Local Services",
};
const RIO_ID = "6ab7dbbeb65e8246afa04607e6";

function RailSkeleton() {
  return (
    <ResponsiveRail>
      {[0, 1, 2, 3].map((i) =>
      <div
        key={i}
        className="h-full flex flex-col">
        
          <div className="aspect-square bg-[#F3F4F6] rounded-[12px] sm:rounded-[16px] animate-pulse" />
          <div className="pt-2.5 pb-3 flex-1 flex flex-col">
            <div className="h-[13px] w-1/2 rounded bg-[#F3F4F6] animate-pulse" />
            <div className="mt-1 min-h-[2.5em]">
              <div className="h-3.5 w-4/5 rounded bg-[#F3F4F6] animate-pulse" />
              <div className="h-3.5 w-3/5 rounded bg-[#F3F4F6] animate-pulse mt-1.5" />
            </div>
            <div className="mt-1.5 h-[13px] w-1/3 rounded bg-[#F3F4F6] animate-pulse" />
            <div className="mt-auto pt-2 min-h-[28px]" />
          </div>
        </div>
      )}
    </ResponsiveRail>);

}

// A row of places with the one in-feed slot sitting after the fourth card.
// Built here rather than inline: a rail reads its children with
// Children.toArray, which flattens arrays but leaves a fragment as a single
// child — a fragment would collapse the whole row into one column.
const feedRow = (items) => {
  const validItems = (items || []).filter((item) => item && item.id && item.title);
  return validItems.map((item) => <ExperienceCard key={item.id} item={item} />);
};

function ListingRail({ title, subtitle, listings, maxItems = RAIL_MAX, priorityCount = 0, preserveOrder = false }) {
  const { nbNames } = useExplore();
  const eligibleListings = (listings || []).filter((item) => item && item.id && item.name);
  const validListings = preserveOrder ? eligibleListings : photosFirst(eligibleListings);
  return (
    <CarouselRail title={title} subtitle={subtitle}>
      {validListings.slice(0, maxItems).map((l, index) => {
        const card = toCard(l, nbNames);
        const priority = index < priorityCount ? (index === 0 ? "high" : "auto") : false;
        return card && card.id && card.title ? <ExperienceCard key={card.id} item={card} priority={priority} /> : null;
      })}
    </CarouselRail>);

}

function DeferredListingRail(props) {
  return <DeferredMount minHeight={330}><ListingRail {...props} /></DeferredMount>;
}

export default function Home() {
  const { t, lang } = useLang();
  const {
    allListings,
    neighborhoods,
    nbNames,
    loading,
    loadError,
    filteredListings,
    tourExperiences,
    hasFilter,
    clearFilters,
    activeChip,
    setActiveChip,
    filterSubs,
    query,
    nearMe
  } = useExplore();

  const location = useLocation();
  // Header/menu and carousel links open an existing in-page category, not a separate route.
  useEffect(() => {
    const section = new URLSearchParams(location.search).get("section");
    const category = section === "stays" ? "Stays" : section === "autos" ? "Auto" : section === "jobs" ? "Jobs" : section === "lgbtq" ? "LGBTQ+" : null;
    if (category) setActiveChip(category);
  }, [location.search, setActiveChip]);

  // Beaches browses as a category, but it holds the editorial guide rather than
  // directory listings, so it renders outside <CategoryView>.
  const isBeachView = activeChip === BEACH_CATEGORY;
  // Services fills its own page with the whole collection rather than rails.
  const isServicesView = activeChip === SERVICES_CATEGORY;
  // Homepage browse labels such as Drink and Play are editorial filter views,
  // not standalone taxonomy pages. Only real taxonomy categories open CategoryView.
  const isMarketplaceView = activeChip === "Stays" || activeChip === "Auto" || activeChip === "Jobs";
  const isCategoryView = CATEGORIES.includes(activeChip) && !isBeachView;
  const categoryOpen = isCategoryView || isBeachView || isMarketplaceView;
  // Once a visitor chooses a browse intent, the hero gently collapses instead
  // of reappearing or forcing the page back to the top. The browse panel stays
  // in place so the transition feels continuous and immediate.
  const heroVisible = activeChip === "Explore";
  const [sortBy, setSortBy] = useState("recommended");

  // Only built when a search or filter is active — the landing page itself no
  // longer lists the whole catalog.
  const results = hasFilter
    ? photosFirst(filteredListings
        .filter((l) => l && l.id && l.name))
        .map((l) => toCard(l, nbNames))
        .filter((card) => card && card.id && card.title)
    : [];

  // Ordering reworks the cards already in hand, so choosing one is instant.
  const sortedResults = useMemo(() => {
    if (sortBy === "recommended") return results;
    const next = [...results];
    if (sortBy === "top") {
      next.sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0) || (Number(b.review_count) || 0) - (Number(a.review_count) || 0));
    } else if (sortBy === "new") {
      next.sort((a, b) => new Date(b.created_date || 0).getTime() - new Date(a.created_date || 0).getTime());
    } else if (sortBy === "az") {
      next.sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), lang === "es" ? "es" : "en"));
    }
    return next;
  }, [results, sortBy, lang]);
  // A short first run of results, then the invitation to keep exploring, then
  // the rest of the list below it.
  const leadResults = sortedResults.slice(0, 9);
  const restResults = sortedResults.slice(9);
  const showRails = !loading && !hasFilter;
  // Keep catalog-derived homepage collections stable between unrelated renders.
  // The catalog can contain 1,000+ records, so repeating these scans on every
  // context update adds avoidable main-thread work on mobile.
  const shopListings = useMemo(() => allListings.filter((item) => catMatches(item, "Shop")), [allListings]);
  const marketListings = useMemo(() => allListings.filter((item) => /market|farmers market|grocery|specialty food|produce/i.test(`${item.category || ""} ${item.subcategory || ""} ${item.name || ""}`)), [allListings]);
  const localMakerListings = useMemo(() => allListings.filter((item) => /artisan craft|local apparel|boutique|independent bookstore|music & records|thrift|vintage|jewelry|souvenir|gift/i.test(`${item.subcategory || ""} ${item.name || ""}`)), [allListings]);
  const localDiscoveryListings = useMemo(() => [...new Map([...marketListings, ...localMakerListings].map((item) => [item.id, item])).values()], [marketListings, localMakerListings]);
  // Assign each listing to ONE homepage rail instead of showing the same
  // restaurant three times. These are presentation slices only; the full
  // collections remain searchable and accessible in their browse categories.
  const chinchorroPicks = useMemo(() => photosFirst(railChinchorros(allListings)).slice(0, 10), [allListings]);
  const foodPicks = useMemo(() => {
    const reserved = new Set(chinchorroPicks.map((item) => String(item.id)));
    return photosFirst(railEatDrink(allListings)).filter((item) => !reserved.has(String(item.id))).slice(0, 10);
  }, [allListings, chinchorroPicks]);
  const localFavorites = useMemo(() => {
    const reserved = new Set([...chinchorroPicks, ...foodPicks].map((item) => String(item.id)));
    const picked = [...new Map([...railTrending(allListings), ...railLocals(allListings)].map((item) => [item.id, item])).values()];
    return photosFirst(picked.filter((item) => !reserved.has(String(item.id)) && !catMatches(item, "Shop"))).slice(0, 10);
  }, [allListings, chinchorroPicks, foodPicks]);
  // Shops, markets and makers read as one discovery row rather than two overlapping ones.
  const shopAndMakers = useMemo(() => [...new Map([...shopListings, ...localDiscoveryListings].map((item) => [item.id, item])).values()], [shopListings, localDiscoveryListings]);

  return (
    <div id="top" className="yts-home-canvas min-h-screen bg-[#FAF9F6] text-[#111111]">
      <SEO
        title="Puerto Rico Local Guide: Things to Do, Food & Culture"
        titleEs="Guía local de Puerto Rico: qué hacer, comida y cultura"
        description="Plan what to do in Puerto Rico with Ya Tú Sabes PR: explore local restaurants, nightlife, cultural spots, neighborhood guides, beaches and bookable experiences across San Juan and beyond."
        descriptionEs="Planifica qué hacer en Puerto Rico con Ya Tú Sabes PR: explora restaurantes locales, vida nocturna, cultura, guías de barrios, playas y experiencias reservables en San Juan y más allá."
        path="/"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Ya Tú Sabes PR",
            alternateName: "Ya Tú Sabes",
            url: "https://yatusabespr.com/",
            description: lang === "es" ? "Guía local de Puerto Rico para explorar lugares, cultura, comida y experiencias." : "Puerto Rico local guide to explore places, culture, food and experiences.",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            inLanguage: lang,
            name: "Ya Tú Sabes PR",
            url: "https://yatusabespr.com/",
            description: lang === "es" ? "Guía local de Puerto Rico para explorar comida, cultura, barrios, eventos y experiencias." : "Puerto Rico local guide to explore food, culture, neighborhoods, events and experiences.",
            potentialAction: {
              "@type": "SearchAction",
              target: { "@type": "EntryPoint", urlTemplate: "https://yatusabespr.com/?q={search_term_string}" },
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      {/* A category opens straight into its own content on a phone, so there the
             hero shows on the landing view only (and always on wider screens). */}
      <div
        className={[
          "overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
          categoryOpen ? "hidden sm:block" : "",
          heroVisible ? "max-h-[720px] opacity-100" : "max-h-0 opacity-0 pointer-events-none",
        ].join(" ")}
      >
        <Hero count={allListings.length} />
      </div>

      <HomeSearchPill />

      <CategoryNav />

      {loadError && !loading && (
        <div role="status" className="mx-4 mt-4 rounded-xl border border-[#d8c9bd] bg-[#fffaf4] px-4 py-3 text-[13px] leading-[1.5] text-[#5d4539] sm:mx-6 lg:mx-auto lg:max-w-[1180px]">
          {t("home.loadError")}
        </div>
      )}

      {/* Keyed on the chip so each category fades in as a fresh view. */}
      <motion.div
        key={activeChip}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}>
        
        {isBeachView && <BeachCategoryView />}

        {isMarketplaceView && (
          <section id="category-results" className="scroll-mt-[132px] bg-[#f7f6f2] text-[#171717] dark:bg-[#131211] dark:text-[#f2efe9]">
            <Suspense fallback={<div className="mx-auto max-w-[1180px] px-4 py-16 text-sm" role="status">{t("common.loading")}</div>}>
              {activeChip === "Stays" ? <HotelsDirectory embedded /> : activeChip === "Jobs" ? <JobsDirectory embedded /> : <AutoDirectory embedded />}
            </Suspense>
          </section>
        )}

        {/* LGBTQ+ is an in-page editorial section of the same local guide. */}
        {activeChip === "LGBTQ+" && <Suspense fallback={<div className="mx-auto max-w-[1180px] px-4 py-12 text-sm">{t("common.loading")}</div>}><LGBTQGuide embedded /></Suspense>}

        {/* Experiences gets its own curated, sortable section; the rest share CategoryView. */}
        {activeChip === EXPERIENCES_CATEGORY && <ExperiencesCategoryView />}

        {/* Services shows every service at once, filled in or still waiting. */}
        {isServicesView &&
        <ServicesCategoryView
          listings={filteredListings}
          loading={loading}
          clearFilters={clearFilters} />

        }

        {/* Category View Override */}
        {isCategoryView && activeChip !== EXPERIENCES_CATEGORY && activeChip !== SERVICES_CATEGORY && activeChip !== "LGBTQ+" &&
        <CategoryView
          category={activeChip}
          listings={filteredListings}
          loading={loading}
          filterSubs={filterSubs}
          clearFilters={clearFilters} />

        }
      </motion.div>

      {!isCategoryView && !isMarketplaceView &&
      <>
          {/* Explore carousels — hidden while search results are showing.
           The page opens with content, never with a sponsor: the first band
           sits under the first row and one follows every row after it. */}
          {showRails &&
        <div className="pb-[calc(96px+env(safe-area-inset-bottom,0px))] sm:pb-16">

              <HomeBookingEdit />
              <HomeLiveMoment />
              <HomeChapterBreak chapter="culture" />
              {neighborhoods.length > 0 &&
                <CarouselRail
                  editorial
                  masthead={lang === "es" ? "EXPLORA POR BARRIO" : "NEIGHBORHOODS / THE LOCAL EDIT"}
                  title={lang === "es" ? "LA EDICIÓN LOCAL." : "THE LOCAL EDIT."}
                  subtitle={lang === "es" ? "Explora barrios, calles y lugares con historia." : "Explore neighborhoods, streets and places with a story."}
                  itemWidth="w-[76vw] max-w-[360px] sm:w-[285px] sm:max-w-none"
                  action={<Link to="/map" className="hidden text-[11px] font-extrabold text-[#77736c] transition-colors hover:text-black sm:inline-flex sm:items-center sm:gap-1 dark:hover:text-white">{lang === "es" ? "Ver en el mapa" : "See on the map"} <ArrowUpRight className="h-3.5 w-3.5" /></Link>}>
                  {neighborhoods.map((n) => <NeighborhoodCard key={n.id} item={n} />)}
                </CarouselRail>
              }
              {/* Lead into the archive with the color and movement of living culture. */}
              <BombaSalsaHomeRail />
              <div id="home-local" className="scroll-mt-[120px]"><RioPiedrasRail /></div>
              <DeferredMount minHeight={300}><Suspense fallback={null}><HomeArtGalleriesRail /></Suspense></DeferredMount>
              <DeferredMount minHeight={280}><Suspense fallback={null}><HomeLGBTQVenuesRail /></Suspense></DeferredMount>
              <BadBunnyFeature />
              <HomeRowAd index={0} />
              <HomeChapterBreak chapter="day" />
              <HorsebackHomeRail />
              <HomeMakeADay />
              <NearMeCard />
              <DeferredMount minHeight={330}><Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}><OffBeatenPathRail /></Suspense></DeferredMount>
              <DeferredMount minHeight={330}><Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}><BeachRail /></Suspense></DeferredMount>
              <HomeRowAd index={1} />
              <HomeChapterBreak chapter="food" />
              <DeferredListingRail title={t("home.foodTitle")} subtitle={t("home.foodSub")} listings={foodPicks} />
              <DeferredMount minHeight={320}><Suspense fallback={null}><CondadoDiningEdit /></Suspense></DeferredMount>
              <DeferredListingRail title={t("home.chinchorrosTitle")} subtitle={t("home.chinchorrosSub")} listings={chinchorroPicks} />
              <DeferredListingRail title={t("home.localPicksTitle")} subtitle={t("home.localPicksSub")} listings={localFavorites} />
              {shopAndMakers.length > 0 && <><DeferredListingRail title={lang === "es" ? "Tiendas, mercados y creadores locales" : "Local Shops & Makers"} subtitle={lang === "es" ? "Boutiques, mercados, librerías y creadores independientes." : "Boutiques, markets, bookstores, and independent makers."} listings={shopAndMakers} maxItems={14} /></>}
              <HomeRowAd index={2} />
              <HomeChapterBreak chapter="night" />
              <DeferredMount minHeight={320}><Suspense fallback={null}><ElBoricuaFeature /></Suspense></DeferredMount>
              <HomeKaraokeRail />
              <DeferredMount minHeight={360}><Suspense fallback={null}><NightlifeRails /></Suspense></DeferredMount>
              <section className="mx-4 my-8 sm:mx-6 lg:mx-auto lg:max-w-[1180px]">
                <div className="mb-4 flex items-end justify-between gap-3"><div><p className="text-[9px] font-black uppercase tracking-[.2em] text-[#8a857b]">{lang === "es" ? "Guías útiles" : "Useful guides"}</p><h2 className="mt-1 text-xl font-black tracking-[-.045em]">{lang === "es" ? "Comunidad" : "Community"}</h2></div><Link to="/districts" className="text-[10px] font-bold text-[#77736c] hover:text-black">{lang === "es" ? "Ver guías" : "Browse guides"} <ArrowUpRight className="inline h-3 w-3" /></Link></div>
                <PrideFeature />
              </section>
              <HomeRowAd index={3} />
              <HomeChapterBreak chapter="more" />
              <FeaturedEventsRail listings={allListings} />
              <HotelsRail />
              <RentalCarsRail />
              <HomeJobsRail lang={lang} />
              <DeferredMount minHeight={330}><Suspense fallback={<div className="min-h-[300px]" aria-hidden="true" />}><FreshFindsRail /></Suspense></DeferredMount>
              <HomeRowAd index={4} />
              <DeferredMount minHeight={150}><Suspense fallback={<div className="min-h-[150px]" aria-hidden="true" />}><div className="pt-8 sm:pt-14 pb-1"><HouseOfPhobiaBanner compact /></div></Suspense></DeferredMount>
            </div>
        }

          {loading &&
        <div className="pb-4">
              <RailSkeleton />
            </div>
        }

          {/* Results only: the landing page doesn't list every place, so this
           section appears solely for an active search or filter. */}
          {!loading && hasFilter &&
        <StaticSection id="category-results" className="pb-[100px] scroll-mt-[132px] sm:scroll-mt-[140px]" >
              {query.trim() && <p className="mb-3 text-[12px] sm:text-[13px] text-[#777773]" aria-live="polite">{lang === "es" ? `Resultados para “${query.trim()}” · ${results.length} lugares` : `Results for “${query.trim()}” · ${results.length} places`}</p>}
              <SectionHead
            title={BROWSE_SECTION_TITLES[activeChip] || (nearMe ? t("nearMe.results", { count: results.length }) : results.length === 1 ? t("results.one") : t("results.many", { count: results.length }))}
            action={
            <button type="button" onClick={clearFilters} className="text-[13px] font-extrabold whitespace-nowrap hover:underline">
                    {t("results.clear")} <span aria-hidden="true">✕</span>
                  </button>
            } />
          
              {results.length === 0 ?
          <div role="status" aria-live="polite" className="text-center py-10 text-[14px] text-[#6B7280]">
                  {t("results.none")}
                  <button type="button" onClick={clearFilters} className="ml-2 font-extrabold text-[#111827] underline">
                    {t("results.clear")}
                  </button>
                </div> :

          <>
            <ResultsSort value={sortBy} onChange={setSortBy} />
            <ResponsiveRail>{feedRow(leadResults)}</ResponsiveRail>
            <KeepExploring count={results.length} />
            {restResults.length > 0 && <ResponsiveRail>{feedRow(restResults)}</ResponsiveRail>}
          </>
          }
            </StaticSection>
        }
        </>
      }
    </div>);

}